/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The ONE tone table of the terms (plan-voorwaarden-vergelijking-2026-10-07
 * §4.1 + §6.0): how one fact compares with what most offers do.
 *
 *   good     — in the visitor's favour, better than the norm → a pluspunt
 *   neutral  — the market norm, or prose (`other`): a fact    → no bullet
 *   watch    — a catch, worse than the norm                   → an aandachtspunt
 *   unknown  — MUTED: not checked, or not stated in the terms → an aandachtspunt
 *              of the "niet gecontroleerd / niet vermeld" family (the review's
 *              own, `*_not_stated`, `smart_not_checked`), never a warning colour
 *
 * THE MARKET NORM STAYS NEUTRAL. That is the rule that ends "24 of 29 rows
 * amber": a deadline of 1 December (one month before the year ends), a
 * contract that renews per year, a payout after the charging year and a
 * required smart charger are what most offers do, so none of them is coloured.
 * Colour marks a DEVIATION — in either direction.
 *
 * "Not stated" is muted like "not checked" (the §6.0 table): both say we could
 * not tell, and the bullet says which. Two deliberate exceptions: on the
 * minimum volume and the price model a silent text is no catch at all
 * (neutral, no bullet), and the inboeker's silence (D24) is muted without one.
 *
 * Every surface reads this table and nothing else: a row line copies its
 * fact's tone (src/lib/termsPresentation.ts), a fact-table value copies it, and
 * the verdict maps good → pluspunt, watch → a watch aandachtspunt, muted → a
 * "niet gecontroleerd / vermeld" aandachtspunt, neutral → nothing
 * (src/lib/termsVerdict.ts). A test pins the three against each other.
 *
 * The tone says how a fact LOOKS; `factState` says what we KNOW (a real code,
 * "not stated", or nothing) — the two are kept apart because "not stated" and
 * "not checked" share a colour but not a meaning.
 *
 * Pure on purpose: no React, no i18n.
 */

import type { PublicDossierEntry, PublicTermsReview, TermsReviewStatus } from '../types';
import { COMMON_CANCEL_DEADLINE, DOSSIER_SPEC, monthDayParts } from './dossier.js';

export type FactTone = 'good' | 'neutral' | 'watch' | 'unknown';

/** The coded dossier fields with a tone. */
export type DossierToneField =
  | 'cancelDeadline'
  | 'afterTerm'
  | 'exitCost'
  | 'payoutTiming'
  | 'inboeker'
  | 'priceModel'
  | 'minimumVolume'
  | 'signupRequirements';

/**
 * Every fact a tone is asked for: the coded dossier fields plus three facts
 * that are not dossier entries — the ROW's looptijd (`duration`), the
 * provider-level smart-charger requirement and the terms review itself.
 */
export type ToneField = DossierToneField | 'duration' | 'smartChargingRequired' | 'termsReview';

export interface ToneContext {
  /** The annual kWh this comparison calculates with — judges a kWh minimum. */
  annualKwh?: number | null;
  /**
   * The record's NEa-list fact. `false` with no `inboeker` fact filed reads as
   * `other` (D24): a party that is not on the closed NEa list can only book
   * through one that is.
   */
  neaListed?: boolean | null;
  /**
   * The ROW's looptijd ('1 jaar'). A renewal "for the same term" of a 1-year
   * offer IS a yearly renewal — the norm, neutral. On a longer looptijd, or
   * where there is no row (the profile page), it stays a catch.
   */
  contractDuration?: unknown;
}

/**
 * Tone per code. `before_date` (by its date), `kwh_per_year` and
 * `charge_points` (by their amount) are decided in factTone itself.
 */
const CODE_TONE: Readonly<Record<DossierToneField, Readonly<Record<string, FactTone>>>> = {
  cancelDeadline: { none: 'good', other: 'neutral', not_stated: 'unknown' },
  afterTerm: {
    ends: 'good',
    renews_yearly: 'neutral',
    // Neutral on a 1-year row, where it is the yearly renewal (factTone).
    renews_same_term: 'watch',
    other: 'neutral',
    not_stated: 'unknown',
  },
  exitCost: {
    none: 'good',
    forfeit_year_payout: 'watch',
    fee: 'watch',
    other: 'neutral',
    not_stated: 'unknown',
  },
  payoutTiming: {
    during_year: 'good',
    after_year: 'neutral',
    other: 'neutral',
    not_stated: 'unknown',
  },
  // D24: booking through another party is a catch (one more company between
  // the visitor and the NEa); "not stated" stays muted — most sites never say.
  inboeker: { self: 'neutral', other: 'watch', not_stated: 'unknown' },
  // The price model is the verdienmodel aandachtspunt's business: never a colour,
  // never a bullet — "not stated" included.
  priceModel: {
    market_minus_commission: 'neutral',
    fixed_per_kwh: 'neutral',
    fixed_per_ere: 'neutral',
    subscription: 'neutral',
    other: 'neutral',
    not_stated: 'neutral',
  },
  // A minimum the terms do not mention is no minimum — so "not stated" is
  // neutral here, unlike on the binding facts. `none` is neutral too: most
  // offers have none. A minimum the terms DO state is a catch, whatever its
  // size (Jasper, 2026-10-08: "unless it IS mentioned, then it is an
  // aandachtspunt") — `other` (a minimum in prose) included; `kwh_per_year`
  // and `charge_points` are judged in factTone.
  minimumVolume: { none: 'neutral', other: 'watch', not_stated: 'neutral' },
  signupRequirements: {},
};

/** The codes whose tone factTone computes from the amount. */
const AMOUNT_TONED = new Set(['before_date', 'kwh_per_year', 'charge_points']);

/** A real `month_day` other than 1 January — the normaliser's rule. */
const isDeadlineDay = (n: number): boolean => !!monthDayParts(n) && n !== 101;

const singleCode = (entry: PublicDossierEntry | null | undefined): string | null =>
  entry && typeof entry.value === 'string' ? entry.value : null;

/**
 * The looptijd of a row as whole calendar years: `'3 jaar'` → 3, `2` → 2;
 * null when unknown or not a positive whole number of years.
 */
export function contractYears(duration: unknown): number | null {
  const n =
    typeof duration === 'number'
      ? duration
      : typeof duration === 'string'
        ? Number.parseInt(duration, 10)
        : Number.NaN;
  return Number.isInteger(n) && n >= 1 ? n : null;
}

/**
 * The review status a payload carries. Absent, null, unknown — and a status
 * without the date it was established on (publicTermsReview's own rule) —
 * read as `not_checked`.
 */
export function termsReviewStatus(
  review: PublicTermsReview | { status?: unknown; checkedAt?: unknown } | null | undefined
): TermsReviewStatus {
  if (!review || typeof review !== 'object') return 'not_checked';
  const { status, checkedAt } = review;
  const dated = typeof checkedAt === 'string' && !Number.isNaN(Date.parse(checkedAt));
  return dated && (status === 'read' || status === 'unreadable' || status === 'not_published')
    ? status
    : 'not_checked';
}

/**
 * The CONTEXT RULE (§5): an offer that ends by itself after its term needs no
 * cancelling, so its cancel deadline is moot — no cancel bullet, and the row
 * says "Opzeggen niet nodig" in the tone of the fact that makes it so.
 */
export function isCancelMoot(
  dossier: Partial<Record<string, PublicDossierEntry>> | null | undefined
): boolean {
  return singleCode(dossier?.afterTerm) === 'ends';
}

/** The binding facts: the ones a `read` review must have answered. */
export const BINDING_FIELDS = ['cancelDeadline', 'afterTerm', 'exitCost'] as const;

/** What we know about one coded fact — the three visible states of §4.1. */
export type FactState = 'known' | 'not_stated' | 'not_checked';

/**
 * The STATE of one coded dossier fact: `known` (a real code, with the amount it
 * needs), `not_stated` (the terms were read and say nothing) or `not_checked`
 * (no usable entry — absent, a legacy code, a code without its amount or with
 * a day that is none). The tone is a different question (factTone).
 */
export function factState(field: DossierToneField, entry: unknown): FactState {
  const e = entry as PublicDossierEntry | null | undefined;
  if (field === 'signupRequirements') {
    return e && (Array.isArray(e.value) ? e.value.length > 0 : !!e.value) ? 'known' : 'not_checked';
  }
  const code = singleCode(e);
  const spec = DOSSIER_SPEC[field];
  if (!code || !spec || !spec.values.includes(code) || spec.legacy.includes(code)) {
    return 'not_checked';
  }
  if (code === 'not_stated') return 'not_stated';
  // A code that takes a number is no fact without it (normalizeDossierValue
  // refuses one; a hand-built object must not slip past as a fact).
  const amount = typeof e?.amount === 'number' && Number.isFinite(e.amount) ? e.amount : null;
  if (spec.amountFor[code] && amount === null) return 'not_checked';
  if (code === 'before_date' && !isDeadlineDay(amount as number)) return 'not_checked';
  return 'known';
}

/**
 * The tone of ONE fact.
 *
 *   dossier fields  `entry` = the public dossier entry: a code's tone, "not
 *                   stated" muted (neutral on the minimum and the price
 *                   model), nothing filed muted (a legacy code reads as
 *                   nothing — publicDossier drops it anyway); `renews_same_term`
 *                   on a 1-year row (ctx.contractDuration) is the yearly
 *                   renewal, neutral
 *   'duration'      `entry` = the row's looptijd ('3 jaar'): ≥ 2 years watch
 *                   (a lock-in beyond the norm, D15), 1 year neutral
 *   'smartChargingRequired'  `entry` = the scalar: false good (no smart charger
 *                   needed), true neutral (the norm), null/absent muted (not
 *                   checked — the verdict says so: `smart_not_checked`)
 *   'termsReview'   `entry` = the public review: read neutral, not_published
 *                   watch (D15), unreadable / not checked muted
 */
export function factTone(field: ToneField, entry: unknown, ctx: ToneContext = {}): FactTone {
  switch (field) {
    case 'duration': {
      const years = contractYears(entry);
      if (years === null) return 'unknown';
      return years >= 2 ? 'watch' : 'neutral';
    }
    case 'smartChargingRequired':
      return entry === false ? 'good' : entry === true ? 'neutral' : 'unknown';
    case 'termsReview': {
      const status = termsReviewStatus(entry as PublicTermsReview | null | undefined);
      if (status === 'read') return 'neutral';
      return status === 'not_published' ? 'watch' : 'unknown';
    }
  }
  const state = factState(field, entry);
  if (state === 'not_checked') {
    // No inboeker fact, on a party that is known NOT to be on the NEa list.
    if (field === 'inboeker' && ctx.neaListed === false) return 'watch';
    return 'unknown';
  }
  if (field === 'signupRequirements') return 'neutral';
  const e = entry as PublicDossierEntry;
  const code = e.value as string;
  const amount = typeof e.amount === 'number' ? e.amount : null;
  if (amount !== null && AMOUNT_TONED.has(code)) {
    if (code === 'before_date') {
      return amount >= COMMON_CANCEL_DEADLINE ? 'neutral' : 'watch';
    }
    // A minimum of one charge point is no minimum; from two it is a catch.
    if (code === 'charge_points') return amount >= 2 ? 'watch' : 'neutral';
    // kwh_per_year: a stated minimum is a catch for every visitor (2026-10-08);
    // whether it exceeds the kWh this comparison calculates with is the
    // bullet's business (termsVerdict picks the wording), not the tone's.
    return amount > 0 ? 'watch' : 'neutral';
  }
  // One year renewing for one year: the yearly renewal, the norm.
  if (
    field === 'afterTerm' &&
    code === 'renews_same_term' &&
    contractYears(ctx.contractDuration) === 1
  ) {
    return 'neutral';
  }
  return CODE_TONE[field][code] ?? 'unknown';
}
